import { useContext, useEffect, useState } from 'react';
import { X } from 'lucide-react';

import { useAuth, useSubscription } from '@chainlit/react-client';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';

import { ChainlitContext } from 'client-types/*';

interface UpgradeRequiredModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const UpgradeRequiredModal = ({ isOpen, onClose }: UpgradeRequiredModalProps) => {
  const apiClient = useContext(ChainlitContext);
  const { user } = useAuth();
  const { createCustomerSession } = useSubscription();
  const [customerSessionSecret, setCustomerSessionSecret] = useState<string>('');
  const [stripeConfig, setStripeConfig] = useState<{
    pricingTableId: string;
    publishableKey: string;
    locale: string;
  } | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const fetchConfig = async () => {
      try {
        const response = await fetch((apiClient as any).buildEndpoint('/api/config'));
        const config = await response.json();
        setStripeConfig(config.stripe);
      } catch {}
    };
    fetchConfig();
  }, [isOpen, apiClient]);

  useEffect(() => {
    if (!isOpen || !user) return;
    const fetchSession = async () => {
      try {
        const session = await createCustomerSession();
        setCustomerSessionSecret(session.client_secret);
      } catch {}
    };
    fetchSession();
  }, [isOpen, user, createCustomerSession]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl w-full">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Uppgradera till Pro</DialogTitle>
        </DialogHeader>

        <div className="min-h-[300px] flex items-center justify-center">
          {customerSessionSecret && stripeConfig ? (
            <div className="w-full">
              <stripe-pricing-table
                pricing-table-id={stripeConfig.pricingTableId}
                publishable-key={stripeConfig.publishableKey}
                customer-session-client-secret={customerSessionSecret}
                data-locale={stripeConfig.locale}
              />
            </div>
          ) : (
            <div className="text-muted-foreground text-sm">Laddar betalningsalternativ...</div>
          )}
        </div>

        <Button
          variant="ghost"
          onClick={onClose}
          className="w-full text-muted-foreground hover:text-foreground"
        >
          <X className="w-4 h-4 mr-2" />
          Stäng
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default UpgradeRequiredModal;
